import { ColDef, GridReadyEvent, ICellRendererParams } from "ag-grid-community";
import { getPastBookingsReport } from "../../../Redux/Reports/ReportsActions";
import { getAllBuildings } from "../../../Redux/Buildings/BuildingActions";
import Loading from "../../../Components/Common/Loading/Loading";
import exportToExcel from "../../../Util/exportToExcel";
import { AppDispatch } from "../../../Redux/store";
import { RootState } from "../../../Redux/store";
import styles from "./PastBookings.module.scss";
import { IoCloseSharp } from "react-icons/io5";
import { FaFilePdf } from "react-icons/fa6";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import moment from "moment";

interface PastBookingsType {
  buildingName: string;
  amenityName: string;
  memberName: string;
  bookingStart: string;
  bookingEnd: string;
  amount: number;
  pdfLink: string;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

function PastBookings() {
  const dispatch = useDispatch<AppDispatch>();
  const [search, setSearch] = useState("");
  const [buildingFilter, setBuildingFilter] = useState("");
  const { pastBookingsReport, pastBookingsReportLoading } = useSelector(
    (state: RootState) => state.reports,
  );

  const { buildings } = useSelector((state: RootState) => state.buildings);

  const [rowData, setRowData] = useState<PastBookingsType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);

  const applyFilters = (rowData: PastBookingsType[]) => {
    let array = [...rowData];

    if (search?.length > 3) {
      array = array.filter(
        (member: PastBookingsType) =>
          member?.buildingName
            ?.toLowerCase()
            ?.includes(search?.toLowerCase()) ||
          member?.amenityName?.toLowerCase()?.includes(search?.toLowerCase()) ||
          member?.memberName?.toLowerCase()?.includes(search?.toLowerCase()) ||
          member?.buildingName?.toLowerCase()?.includes(search?.toLowerCase()),
      );
    }

    if (buildingFilter) {
      array = array.filter(
        (member: PastBookingsType) => member?.buildingName === buildingFilter,
      );
    }

    return array;
  };

  const filteredRowData = applyFilters(rowData);

  const columnDefs: ColDef<PastBookingsType>[] = [
    {
      headerName: "Building Name",
      flex: 1,
      minWidth: 150,
      field: "buildingName",
    },
    {
      headerName: "Amenity Name",
      field: "amenityName",
      flex: 1,
      minWidth: 150,
    },

    {
      headerName: "Member Name",
      field: "memberName",
      flex: 1,
      minWidth: 150,
    },

    {
      headerName: "Booking Start Time",
      field: "bookingStart",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) =>
        moment(params?.data?.bookingStart).format("MM/DD/YYYY hh:mm A"),
    },

    {
      headerName: "Booking End Time",
      field: "bookingEnd",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) =>
        moment(params?.data?.bookingEnd).format("MM/DD/YYYY hh:mm A"),
    },

    {
      headerName: "Amount",
      field: "amount",
      flex: 1,
      minWidth: 100,
      valueGetter: (params) => `$ ${params?.data?.amount}`,
    },

    {
      headerName: "PDF",
      flex: 1,
      minWidth: 100,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<PastBookingsType>) => {
        return (
          <div className={styles.pdfContainer}>
            <a
              href={params?.data?.pdfLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaFilePdf className={styles.pdfIcon} size={20} />
            </a>
          </div>
        );
      },
    },
  ];

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getPastBookingsReport());
    dispatch(getAllBuildings());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pastBookingsReport) {
      setRowData(pastBookingsReport);
    }
  }, [pastBookingsReport]);

  if (pastBookingsReportLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingIcon}>
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.topContainer}>
        <div className={styles.headingContainer}>
          <h1 className={styles.heading}>Past Bookings</h1>
        </div>
        <div className={styles.buttonContainer}>
          {filteredRowData?.length > 0 && (
            <button
              className={styles.button}
              onClick={() =>
                exportToExcel({
                  data: filteredRowData || [],
                  fileName: "Past Bookings Report",
                })
              }
            >
              Export
            </button>
          )}
        </div>
      </div>
      <div className={styles.bodyContainer}>
        <div className={styles.doubleInputContainer}>
          <div className={styles.searchContainer}>
            <div className={styles.searchContainerInner}>
              <input
                type="text"
                className={styles.input}
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e?.target?.value || "")}
              />
              <div
                className={styles.iconContainer}
                onClick={() => setSearch("")}
              >
                <IoCloseSharp className={styles.icon} />
              </div>
            </div>
          </div>
          <div className={styles.filtersContainer}>
            <div className={styles.filterItem}>
              <p className={styles.label}>Building</p>
              <select
                className={styles.input}
                value={buildingFilter}
                onChange={(e) => setBuildingFilter(e?.target?.value || "")}
              >
                <option value="">All</option>
                {buildings?.map((building: any, i: number) => {
                  return (
                    <option key={i} value={building?.buildingName}>
                      {building?.buildingName}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>
        <div className={styles.gridContainer}>
          <AgGridReact
            className={styles.grid}
            rowData={filteredRowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[5, 10, 20, 50, 100]}
            animateRows={true}
            domLayout="autoHeight"
          />
        </div>
      </div>
    </div>
  );
}

export default PastBookings;
